import {useCallback, useMemo, useState} from 'react';

import {AI_DEFAULTS, API_CONFIG, ERROR_MESSAGES} from '../config';
import {
    normalizeHighlightTokens,
    restoreFormulaTextFromHighlights,
    splitHighlightPhrases,
} from '../highlightUtils';
import aiService from '../services/ai/aiService';
import {
    extractUniqueFormulaVarNames,
    resolveForEditing,
    unresolveFromEditing,
} from '../formulaUtils';

const collectFormulaMappings = (...mappingGroups) => {
    const seen = new Set();

    return mappingGroups
        .flat(Infinity)
        .filter(
            (mapping) =>
                mapping?.original &&
                mapping?.resolved &&
                mapping.original !== mapping.resolved,
        )
        .filter((mapping) => {
            const key = `${mapping.original}::${mapping.resolved}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

const restoreFormulaTokens = (text, mappings) => {
    if (typeof text !== 'string' || !text || !mappings.length) return text;
    const exactRestored = unresolveFromEditing(text, mappings);

    return restoreFormulaTextFromHighlights(
        exactRestored,
        mappings.map((mapping) => ({
            resolved: mapping?.resolved || '',
            original: mapping?.original || '',
        })),
        {minLength: 1},
    );
};

const normalizeFieldKey = (value = '') =>
    String(value)
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

const createMappingHighlightTokens = (mappings = [], source) =>
    mappings.map((mapping) => ({
        resolved: mapping?.resolved || '',
        original: mapping?.original || mapping?.resolved || '',
        source,
    }));

const createResolvedHighlightTokens = (values = [], source) =>
    values.map((value) => ({
        resolved: value,
        original: value,
        source,
    }));

const assignGeneratedBulletsToRows = (generatedBullets = [], resolvedRows = []) => {
    if (!generatedBullets.length || !resolvedRows.length) {
        return generatedBullets.map(() => null);
    }

    const remainingRowIndexes = new Set(resolvedRows.map((_, index) => index));
    const assignments = new Array(generatedBullets.length).fill(null);

    generatedBullets.forEach((bullet, bulletIndex) => {
        const bulletFieldKey = normalizeFieldKey(bullet?.field || '');
        if (!bulletFieldKey) return;

        const matchingRowIndexes = resolvedRows
            .map((row, rowIndex) => ({row, rowIndex}))
            .filter(
                ({row, rowIndex}) =>
                    remainingRowIndexes.has(rowIndex) &&
                    normalizeFieldKey(row.field) === bulletFieldKey,
            )
            .map(({rowIndex}) => rowIndex);

        if (matchingRowIndexes.length !== 1) return;

        assignments[bulletIndex] = matchingRowIndexes[0];
        remainingRowIndexes.delete(matchingRowIndexes[0]);
    });

    const fallbackRowIndexes = [...remainingRowIndexes].sort((left, right) => left - right);

    assignments.forEach((assignedRowIndex, bulletIndex) => {
        if (assignedRowIndex !== null) return;

        const nextRowIndex = fallbackRowIndexes.shift();
        if (nextRowIndex === undefined) return;
        assignments[bulletIndex] = nextRowIndex;
    });

    return assignments.map((rowIndex) =>
        rowIndex === null ? null : resolvedRows[rowIndex] || null,
    );
};

const buildGenerationHighlights = ({
    enabled,
    topicMappings,
    descriptionContextMappings,
    resolvedRows,
    generatedResult,
}) => {
    const title = enabled?.title
        ? normalizeHighlightTokens(createMappingHighlightTokens(topicMappings, 'topic'))
        : [];
    const description = enabled?.desc
        ? normalizeHighlightTokens([
              ...createMappingHighlightTokens(topicMappings, 'topic'),
              ...createMappingHighlightTokens(
                  descriptionContextMappings,
                  'description_context',
              ),
          ])
        : [];
    const bulletAssignments = assignGeneratedBulletsToRows(
        generatedResult?.bullets || [],
        resolvedRows,
    );
    const bullets = enabled?.bullets
        ? bulletAssignments.map((row) =>
              row
                  ? normalizeHighlightTokens([
                        ...createMappingHighlightTokens(
                            row.keywordMappings,
                            'bullet_keywords',
                        ),
                        ...createMappingHighlightTokens(row.fieldMappings, 'bullet_field'),
                        ...createResolvedHighlightTokens(
                            splitHighlightPhrases(row.kw),
                            'bullet_keywords',
                        ),
                    ])
                  : [],
          )
        : [];
    const faqs = enabled?.faqs
        ? (generatedResult?.faqs || []).map((_faq, index) => {
              const row = resolvedRows[index] || null;
              return row
                  ? normalizeHighlightTokens([
                        ...createMappingHighlightTokens(row.keywordMappings, 'faq_keywords'),
                        ...createMappingHighlightTokens(row.fieldMappings, 'faq_field'),
                        ...createResolvedHighlightTokens(
                            splitHighlightPhrases(row.kw),
                            'faq_keywords',
                        ),
                    ])
                  : [];
          })
        : [];

    return {
        title,
        description,
        bullets,
        faqs,
    };
};

export const useAI = (overrideSettings = {}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const aiSettings = useMemo(() => ({...AI_DEFAULTS, ...overrideSettings}), [overrideSettings]);

    const clearError = useCallback(() => setError(null), []);

    const generateAiContent = useCallback(
        async ({
            apiKey,
            topic,
            rows,
            vars,
            tone,
            usecase,
            audience,
            enabled,
            profileSettings,
            descriptionContext = '',
            referenceImages = [],
            brandContext = '',
            openingPattern = null,
        }) => {
            const validRows = rows.filter((row) => row.field || row.kw);
            if (enabled?.bullets && !validRows.length) {
                const nextError = 'Add at least one bullet field.';
                setError(nextError);
                throw new Error(nextError);
            }

            setLoading(true);
            setError(null);

            try {
                const topicResolution = resolveForEditing(topic, vars);
                const resolvedRows = validRows.map((row) => {
                    const fieldResolution = resolveForEditing(row.field, vars);
                    const keywordResolution = resolveForEditing(row.kw, vars);
                    const referencedVarNames = Array.from(
                        new Set([
                            ...extractUniqueFormulaVarNames(row.field || ''),
                            ...extractUniqueFormulaVarNames(row.kw || ''),
                        ]),
                    );

                    return {
                        ...row,
                        field: fieldResolution.resolved,
                        kw: keywordResolution.resolved,
                        referencedVarNames,
                        fieldMappings: fieldResolution.mappings,
                        keywordMappings: keywordResolution.mappings,
                        mappings: [...fieldResolution.mappings, ...keywordResolution.mappings],
                    };
                });
                const descriptionContextResolution = resolveForEditing(descriptionContext, vars);
                const formulaMappings = collectFormulaMappings(
                    topicResolution.mappings,
                    resolvedRows.map((row) => row.mappings),
                    descriptionContextResolution.mappings,
                );
                const promptRows = resolvedRows.map(
                    ({
                        fieldMappings: _fieldMappings,
                        keywordMappings: _keywordMappings,
                        mappings: _mappings,
                        ...row
                    }) => row,
                );
                const validReferenceImages = referenceImages.filter(
                    (image) => typeof image?.url === 'string' && image.url.trim(),
                );

                const prompt = aiService.buildPrompt({
                    resolvedTopic: topicResolution.resolved,
                    tone,
                    usecase,
                    audience,
                    enabled,
                    profileSettings,
                    resolvedRows: promptRows,
                    descriptionContext: descriptionContextResolution.resolved,
                    referenceImageCount: validReferenceImages.length,
                    vars,
                    brandContext,
                    openingPattern,
                });

                const restoreGeneratedResult = (result) => ({
                    ...result,
                    title: restoreFormulaTokens(result?.title || '', formulaMappings),
                    desc: restoreFormulaTokens(result?.desc || '', formulaMappings),
                    bullets: Array.isArray(result?.bullets)
                        ? result.bullets.map((bullet) => ({
                              ...bullet,
                              field: restoreFormulaTokens(bullet?.field || '', formulaMappings),
                              text: restoreFormulaTokens(bullet?.text || '', formulaMappings),
                          }))
                        : [],
                    faqs: Array.isArray(result?.faqs)
                        ? result.faqs.map((faq) => ({
                              // Mirror the bullets/title/desc treatment: the
                              // AI sees keywords with resolved values
                              // (e.g. "Brushed Nickel"); on the way back we
                              // re-introduce the {{var}} tokens so the same
                              // FAQ resolves correctly across every variant
                              // in the group.
                              question: restoreFormulaTokens(
                                  faq?.question || '',
                                  formulaMappings,
                              ),
                              answer: restoreFormulaTokens(
                                  faq?.answer || '',
                                  formulaMappings,
                              ),
                          }))
                        : [],
                });

                const buildResolvedPayload = (result) => {
                    const generationHighlights = buildGenerationHighlights({
                        enabled,
                        topicMappings: topicResolution.mappings,
                        descriptionContextMappings: descriptionContextResolution.mappings,
                        resolvedRows,
                        generatedResult: result,
                    });

                    return {
                        ...restoreGeneratedResult(result),
                        generationHighlights,
                    };
                };

                if (API_CONFIG.ENABLE_AI_FALLBACK) {
                    await new Promise((resolve) => setTimeout(resolve, 180));

                    return buildResolvedPayload(
                        aiService.createFallbackGeneratedResult({
                            resolvedTopic: topicResolution.resolved,
                            resolvedRows: promptRows,
                            enabled,
                            faqCount:
                                enabled.faqs
                                    ? promptRows.length || profileSettings?.faqs?.faqCount || 4
                                    : 0,
                        }),
                    );
                }

                // Bump maxTokens to fit the JSON payload — the base
                // budget assumes title + description only. Each extra
                // section adds significant text, and a truncated response
                // surfaces as a "model response was not valid JSON" error.
                const sectionBudget =
                    (enabled.title ? 150 : 0) +
                    (enabled.desc ? 350 : 0) +
                    (enabled.bullets ? Math.max(resolvedRows.length, 1) * 120 : 0) +
                    (enabled.faqs
                        ? (resolvedRows.length || profileSettings?.faqs?.faqCount || 4) * 220
                        : 0);
                const dynamicMaxTokens = Math.max(
                    aiSettings.maxTokens,
                    sectionBudget + 200,
                );

                return buildResolvedPayload(
                    await aiService.requestGeneratedContent({
                        apiKey,
                        prompt,
                        model: aiSettings.model,
                        maxTokens: dynamicMaxTokens,
                        inputImages: validReferenceImages,
                    }),
                );
            } catch (generationError) {
                const nextError =
                    generationError.message || ERROR_MESSAGES.AI_GENERATION_FAILED;
                setError(nextError);
                throw generationError instanceof Error
                    ? generationError
                    : new Error(nextError);
            } finally {
                setLoading(false);
            }
        },
        [aiSettings],
    );

    return {
        generateAiContent,
        loading,
        error,
        clearError,
        aiSettings,
    };
};
